import { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'

interface User {
  userId: string
  userName: string
  userAvatar?: string
  userEmail?: string
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: lumi.auth.user,
    isAuthenticated: lumi.auth.isAuthenticated,
    isLoading: false
  })

  useEffect(() => {
    // Listen to auth state changes
    const unsubscribe = lumi.auth.onAuthChange(({ isAuthenticated, user }) => {
      setAuthState({
        user: user || null,
        isAuthenticated,
        isLoading: false
      })
    })

    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [])

  const signIn = async () => {
    setAuthState(prev => ({ ...prev, isLoading: true }))
    try {
      const { user } = await lumi.auth.signIn()
      setAuthState({
        user: user || null,
        isAuthenticated: true,
        isLoading: false
      })
    } catch (error) {
      console.error('Login failed:', error)
      setAuthState(prev => ({ ...prev, isLoading: false }))
    }
  }

  const signOut = async () => {
    setAuthState(prev => ({ ...prev, isLoading: true }))
    try {
      await lumi.auth.signOut()
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false
      })
    } catch (error) {
      console.error('Logout failed:', error)
      setAuthState(prev => ({ ...prev, isLoading: false }))
    }
  }

  return {
    user: authState.user,
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    signIn,
    signOut
  }
}